/**
 * The way in.
 *
 * Five states in one panel rather than five screens: sign in, sign up, the
 * second factor, and the two halves of the forgotten-password detour. They
 * share the card, the error line and the busy state, because they are one
 * errand - getting into the account - and bouncing between routes mid-sign-in
 * loses what you typed.
 *
 * The left half is the pitch, and it is alive: real cover art from the game
 * catalogue drifting past, and real numbers underneath it. Nothing here is
 * invented - the counts come from the same public endpoint the parties page
 * uses, and when the server has not answered yet the strip simply is not
 * drawn. An empty room is worth admitting to; a fake one is not.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@/stores/session';
import { DEFAULT_SERVER, api, endpoints, serverUrl, setServerUrl } from '@/lib/api';
import { gameBanner, gameIconArt } from '@/lib/gameArt';
import type { Catalog } from '@/lib/types';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

type Stage = 'in' | 'up' | 'code' | 'forgot' | 'reset';

/* The site the app is pointed at, trailing slashes trimmed.
 *
 * The legal pages are plain HTML served by the same server, not routes
 * inside the app, so they need a real URL rather than a hash - and the
 * desktop build can be pointed at a different origin, so it cannot be a
 * bare path either. */
const legalBase = () => serverUrl().replace(/[/]+$/, '');

export function AuthView() {
  const signIn = useSession((s) => s.signIn);
  const signUp = useSession((s) => s.signUp);
  const adopt = useSession((s) => s.adopt);
  const catalog = useSession((s) => s.catalog);

  const [stage, setStage] = useState<Stage>('in');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  /** Carried between the two halves of a two-factor sign-in. */
  const [ticket, setTicket] = useState('');
  const [method, setMethod] = useState<'app' | 'email'>('app');
  const [hint, setHint] = useState<string | null>(null);

  function go(next: Stage) {
    setStage(next);
    setError(null);
    setNote(null);
    setCode('');
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNote(null);

    try {
      if (stage === 'in') {
        const res = await signIn(username.trim().toLowerCase(), password);
        // The password was right, but the account wants a code as well.
        if (res?.twoFactor && res.ticket) {
          setTicket(res.ticket);
          setMethod(res.method === 'email' ? 'email' : 'app');
          setHint(res.hint ?? null);
          go('code');
        }
      } else if (stage === 'up') {
        await signUp(username.trim().toLowerCase(), password, email.trim(), undefined);
      } else if (stage === 'code') {
        const res = await endpoints.twoFactor(ticket, code.trim());
        if (res.token && res.user) await adopt(res.token, res.user);
      } else if (stage === 'forgot') {
        await endpoints.forgot(email.trim());
        go('reset');
        // Said the same way whether or not the address has an account - the
        // server does not distinguish, and neither does this.
        setNote('If that address has an account, a code is on its way to it.');
      } else if (stage === 'reset') {
        const res = await endpoints.resetPassword(email.trim(), code.trim(), password);
        await adopt(res.token, res.user);
      }
    } catch (err) {
      const failure = err as Error & { ticket?: string };
      // A mistyped code hands back a fresh ticket rather than sending them
      // back to the password screen.
      if (stage === 'code' && failure.ticket) setTicket(failure.ticket);
      setError(failure.message);
    } finally {
      setBusy(false);
    }
  }

  const heads: Record<Stage, [string, string]> = {
    in: ['Welcome back', 'Sign in and get back to your squads.'],
    up: ['Make an account', 'Takes about twenty seconds.'],
    code: [
      'One more step',
      method === 'email'
        ? 'We sent a code to ' + (hint || 'your email') + '.'
        : 'Open your authenticator app and type the code it shows.'
    ],
    forgot: ['Forgotten password', 'Tell us the address on the account.'],
    reset: ['Set a new password', 'The code from your email, then whatever you want the password to be.']
  };

  const [title, blurb] = heads[stage];

  const ready =
    stage === 'in' ? !!username && !!password
      : stage === 'up' ? !!username && !!email && password.length >= 8
        : stage === 'code' ? code.trim().length >= 6
          : stage === 'forgot' ? !!email
            : code.trim().length >= 6 && password.length >= 8;

  return (
    <div className="relative grid min-h-0 flex-1 overflow-hidden [grid-template-rows:1fr] lg:grid-cols-[1.05fr_minmax(430px,.95fr)]">
      <Showcase catalog={catalog} />

      <section className="relative z-10 grid h-full place-items-center overflow-y-auto border-line-soft bg-surface-0 px-6 py-10 lg:border-l">
        <form onSubmit={submit} className="w-full max-w-[380px]">
          {/* the mark, small, so the panel has an owner on a phone too */}
          <div className="mb-7 flex items-center gap-2.5 lg:hidden">
            <img src="./logo.png" alt="" width={30} height={30} className="rounded-[calc(10px*var(--radius-mul,1))]" />
            <span className="text-[15px] font-extrabold tracking-[.24em]">PUGO</span>
          </div>

          <Steps stage={stage} />

          <h2 className="mt-5 text-[26px] leading-tight font-extrabold tracking-tight">{title}</h2>
          <p className="mt-1.5 text-[13.5px] text-mute">{blurb}</p>

          <div key={stage} className="panel-in mt-6 flex flex-col gap-4">
            {stage === 'in' || stage === 'up' ? (
              <>
                <Field
                  label={stage === 'in' ? 'Username or email' : 'Username'}
                  icon="profile"
                  value={username}
                  onChange={setUsername}
                  autoComplete="username"
                  placeholder={stage === 'in' ? 'you, or you@gmail.com' : 'what your squad calls you'}
                />

                {stage === 'up' ? (
                  <Field
                    label="Email"
                    icon="inbox"
                    type="email"
                    value={email}
                    onChange={setEmail}
                    autoComplete="email"
                    placeholder="you@gmail.com"
                    hint="How you get back in if you forget your password."
                  />
                ) : null}

                <Field
                  label="Password"
                  icon="lock"
                  type="password"
                  value={password}
                  onChange={setPassword}
                  autoComplete={stage === 'in' ? 'current-password' : 'new-password'}
                  placeholder={stage === 'up' ? 'at least 8 characters' : ''}
                  meter={stage === 'up'}
                />
              </>
            ) : null}

            {stage === 'code' ? (
              <>
                <div>
                  <Label>{method === 'email' ? 'Code from your email' : 'Code from your app'}</Label>
                  <CodeInput value={code} onChange={setCode} />
                </div>
                <p className="-mt-1 text-[12px] text-ghost">
                  Lost your phone? Type one of your recovery codes instead.
                </p>
              </>
            ) : null}

            {stage === 'forgot' || stage === 'reset' ? (
              <Field
                label="Email"
                icon="inbox"
                type="email"
                value={email}
                onChange={setEmail}
                autoComplete="email"
                placeholder="you@gmail.com"
              />
            ) : null}

            {stage === 'reset' ? (
              <>
                <div>
                  <Label>Code from your email</Label>
                  <CodeInput value={code} onChange={setCode} autoFocus={false} />
                </div>
                <Field
                  label="New password"
                  icon="lock"
                  type="password"
                  value={password}
                  onChange={setPassword}
                  autoComplete="new-password"
                  meter
                />
              </>
            ) : null}
          </div>

          {note ? (
            <p className="mt-4 flex items-start gap-2 rounded-[calc(10px*var(--radius-mul,1))] border border-[color-mix(in_srgb,var(--good)_35%,transparent)] bg-[color-mix(in_srgb,var(--good)_10%,transparent)] px-3 py-2.5 text-[12.5px] text-good">
              <Icon name="checkCirc" size={14} className="mt-0.5 flex-none" />
              {note}
            </p>
          ) : null}

          {error ? (
            <p className="mt-4 flex items-start gap-2 rounded-[calc(10px*var(--radius-mul,1))] border border-[color-mix(in_srgb,var(--bad)_35%,transparent)] bg-[color-mix(in_srgb,var(--bad)_10%,transparent)] px-3 py-2.5 text-[12.5px] font-semibold text-bad">
              <Icon name="alert" size={14} className="mt-0.5 flex-none" />
              {error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={busy || !ready}
            className={cn(
              'mt-6 flex h-[50px] w-full items-center justify-center gap-2 rounded-[calc(14px*var(--radius-mul,1))] text-[15px] font-bold text-accent-fg',
              'bg-[linear-gradient(180deg,var(--accent-hi),var(--accent))]',
              'shadow-[0_0_0_1px_color-mix(in_srgb,var(--accent-hi)_55%,transparent),0_12px_30px_-10px_var(--accent)]',
              'transition-[background-color,transform] duration-150 hover:bg-accent-hi active:scale-[.99]',
              'disabled:pointer-events-none disabled:opacity-35 disabled:shadow-none'
            )}
          >
            {busy ? (
              <>
                <Spinner />
                One moment…
              </>
            ) : (
              <>
                {stage === 'in' ? 'Sign in'
                  : stage === 'up' ? 'Create account'
                    : stage === 'code' ? 'Confirm'
                      : stage === 'forgot' ? 'Send the code'
                        : 'Change my password'}
                <Icon name="arrowR" size={16} />
              </>
            )}
          </button>

          {/* the ways out of each stage */}
          <div className="mt-5 text-center">
            {stage === 'in' ? (
              <>
                <p className="text-[13px] text-mute">
                  New here?{' '}
                  <button type="button" onClick={() => go('up')} className="font-bold text-accent-hi hover:underline">
                    Create an account
                  </button>
                </p>
                <button
                  type="button"
                  onClick={() => go('forgot')}
                  className="mt-2 text-[12.5px] text-ghost transition-colors hover:text-mute hover:underline"
                >
                  Forgotten your password?
                </button>
              </>
            ) : (
              <button type="button" onClick={() => go('in')} className="text-[13px] font-bold text-accent-hi hover:underline">
                {stage === 'up' ? 'Already have an account? Sign in' : 'Back to sign in'}
              </button>
            )}
          </div>

          <p className="mt-8 text-center text-[11.5px] text-ghost">
            {serverUrl().replace(/^https?:\/\//, '')}
            <button
              type="button"
              className="ml-1.5 font-semibold text-mute transition-colors hover:text-dim hover:underline"
              onClick={() => {
                const next = prompt('Server address', serverUrl());
                if (next !== null) { setServerUrl(next || DEFAULT_SERVER); location.reload(); }
              }}
            >
              change
            </button>
          </p>

          {/*
            Reachable from the one screen everybody sees before they have an
            account. A privacy policy nobody can find before signing up is a
            policy that was written to be pointed at, not read.
          */}
          <p className="mt-3 text-center text-[11.5px] text-ghost">
            <a
              href={legalBase() + '/privacy'}
              target="_blank"
              rel="noreferrer"
              className="text-mute transition-colors hover:text-dim hover:underline"
            >
              Privacy
            </a>
            <span className="mx-1.5 text-ghost">·</span>
            <a
              href={legalBase() + '/terms'}
              target="_blank"
              rel="noreferrer"
              className="text-mute transition-colors hover:text-dim hover:underline"
            >
              Terms
            </a>
            <span className="mx-1.5 text-ghost">·</span>
            <a
              href={legalBase() + '/status'}
              target="_blank"
              rel="noreferrer"
              className="text-mute transition-colors hover:text-dim hover:underline"
            >
              Status
            </a>
          </p>
        </form>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------ the pitch */

/**
 * The left half: cover art in slow motion, and what the place looks like now.
 *
 * Two columns drifting in opposite directions at a crawl. It is decoration,
 * so it is the first thing to go when somebody has asked for less movement -
 * the reduce-motion class parks both columns where they are.
 */
function Showcase({ catalog }: { catalog: Catalog | null }) {
  const games = useMemo(
    () => (catalog?.games ?? []).filter((g) => gameBanner(g.id) || gameIconArt(g.id)),
    [catalog]
  );

  /*
   * Three interleaved columns, each doubled.
   *
   * Doubled because the animation slides a column exactly half its own height
   * and starts over - so the second copy is what is on screen as the first
   * one leaves, and there is no seam to catch. Three rather than two so the
   * art reaches both edges instead of sitting in a strip down the middle.
   */
  const columns = useMemo(() => {
    if (!games.length) return [[], [], []] as (typeof games)[];
    return [0, 1, 2].map((n) => {
      const slice = games.filter((_, i) => i % 3 === n);
      return [...slice, ...slice];
    });
  }, [games]);

  return (
    <section className="relative hidden h-full overflow-hidden bg-surface-1 lg:block">
      {/* the art */}
      <div className="absolute inset-0 flex gap-4 opacity-[.45]">
        {columns.map((column, i) => (
          <div
            key={i}
            className={cn(
              'flex min-w-0 flex-1 flex-col gap-4',
              // The middle column runs the other way, so the wall of art is
              // never one block sliding as a unit.
              i === 1
                ? 'animate-[drift-down_64s_linear_infinite] -mt-[200px]'
                : 'animate-[drift-up_58s_linear_infinite]'
            )}
          >
            {column.map((game, j) => {
              const art = gameBanner(game.id) ?? gameIconArt(game.id);
              return (
                <div
                  key={game.id + j}
                  className="h-[250px] w-full flex-none overflow-hidden rounded-[calc(14px*var(--radius-mul,1))] border border-line-soft bg-surface-2"
                >
                  {art ? (
                    <img src={art} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                  ) : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Washes over the top, so the words never sit on a busy patch of art. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'linear-gradient(180deg, var(--bg-1) 0%, color-mix(in srgb, var(--bg-1) 55%, transparent) 26%, color-mix(in srgb, var(--bg-1) 88%, transparent) 62%, var(--bg-1) 100%)'
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(75% 55% at 12% 78%, color-mix(in srgb, var(--accent) 26%, transparent), transparent 72%)' }}
      />

      {/* the words */}
      <div className="relative flex h-full flex-col justify-between p-12">
        <div className="flex items-center gap-3">
          <img src="./logo.png" alt="" width={34} height={34} className="rounded-[calc(10px*var(--radius-mul,1))]" />
          <span className="text-[16px] font-extrabold tracking-[.26em]">PUGO</span>
        </div>

        <div>
          <h1 className="max-w-[13ch] text-[52px] leading-[.98] font-extrabold tracking-[-.035em]">
            Stop playing <span className="text-accent-hi">solo</span>.
          </h1>
          <p className="mt-5 max-w-[42ch] text-[15.5px] leading-relaxed text-dim">
            Post what you are queueing, fill the empty seats, and drop straight into voice.
            No invite links, no second app.
          </p>

          <ul className="mt-8 flex flex-wrap gap-x-7 gap-y-3">
            {([
              ['bolt', 'Parties that fill'],
              ['headset', 'Voice built in'],
              ['badges', 'Levels and badges'],
              ['lock', 'Two-factor, if you want it']
            ] as const).map(([icon, label]) => (
              <li key={label} className="flex items-center gap-2 text-[13.5px] font-semibold text-dim">
                <Icon name={icon} size={15} className="text-accent-hi" />
                {label}
              </li>
            ))}
          </ul>
        </div>

        <LiveStrip />
      </div>

      <style>{`
        @keyframes drift-up { from { transform: translateY(0) } to { transform: translateY(-50%) } }
        @keyframes drift-down { from { transform: translateY(-50%) } to { transform: translateY(0) } }
        .reduce-motion [class*="drift-"] { animation: none !important }
      `}</style>
    </section>
  );
}

/**
 * What the place actually looks like, right now.
 *
 * Counted from the same public endpoint the parties page uses. If it does not
 * answer, the strip is not drawn - a sign-in screen claiming five thousand
 * players online is the sort of thing you only get to be wrong about once.
 */
function LiveStrip() {
  const [stats, setStats] = useState<{ parties: number; players: number; games: number } | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const [health, cat] = await Promise.all([
          api.get<{ online: number; parties: number }>('/api/health'),
          api.get<Catalog>('/api/catalog')
        ]);
        if (!alive) return;
        setStats({ parties: health.parties ?? 0, players: health.online ?? 0, games: cat.games?.length ?? 0 });
      } catch {
        /* no numbers rather than made-up ones */
      }
    })();
    return () => { alive = false; };
  }, []);

  if (!stats) return <div className="h-[54px]" />;

  const cells: [number, string][] = [
    [stats.parties, stats.parties === 1 ? 'party open' : 'parties open'],
    [stats.players, stats.players === 1 ? 'player online' : 'players online'],
    [stats.games, 'games supported']
  ];

  return (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-3 rounded-[calc(16px*var(--radius-mul,1))] border border-line-soft bg-[color-mix(in_srgb,var(--bg-0)_62%,transparent)] px-6 py-4 backdrop-blur-sm">
      {cells.map(([value, label]) => (
        <span key={label} className="flex items-baseline gap-2">
          <span className="text-[21px] leading-none font-extrabold tabular-nums">{value.toLocaleString()}</span>
          <span className="text-[12.5px] whitespace-nowrap text-mute">{label}</span>
        </span>
      ))}
      <span className="ml-auto flex items-center gap-1.5 text-[12px] font-semibold text-good">
        <span className="h-[7px] w-[7px] rounded-full bg-good" />
        Live
      </span>
    </div>
  );
}

/* ------------------------------------------------------------- the parts */

/** Where you are in the errand, when the errand has more than one step. */
function Steps({ stage }: { stage: Stage }) {
  const flow: Stage[] =
    stage === 'code' ? ['in', 'code']
      : stage === 'forgot' || stage === 'reset' ? ['forgot', 'reset']
        : [];
  if (!flow.length) return null;

  const at = flow.indexOf(stage);

  return (
    <div className="flex items-center gap-2">
      {flow.map((step, i) => (
        <span
          key={step}
          className={cn(
            'h-[3px] flex-1 rounded-full transition-colors duration-300',
            i <= at ? 'bg-accent' : 'bg-surface-3'
          )}
        />
      ))}
      <span className="ml-1 text-[11px] font-bold text-ghost tabular-nums">
        {at + 1}/{flow.length}
      </span>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-2 block text-[10.5px] font-bold tracking-[.08em] text-mute uppercase">
      {children}
    </span>
  );
}

/**
 * One labelled input, with the icon inside it.
 *
 * A password field on the sign-up step also gets a strength bar - not a score
 * out of five with rules to satisfy, just a hint that longer is better, which
 * is the only advice that actually holds.
 */
function Field({ label, icon, value, onChange, hint, meter, ...rest }: {
  label: string;
  icon: string;
  value: string;
  onChange: (next: string) => void;
  hint?: string;
  meter?: boolean;
  type?: string;
  autoComplete?: string;
  placeholder?: string;
}) {
  const [shown, setShown] = useState(false);
  const isPassword = rest.type === 'password';

  return (
    <div>
      <Label>{label}</Label>
      <div className="relative">
        <Icon
          name={icon}
          size={15}
          className="pointer-events-none absolute top-1/2 left-3.5 -translate-y-1/2 text-ghost"
        />
        <input
          {...rest}
          type={isPassword && shown ? 'text' : rest.type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          className={cn(
            'h-[46px] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-2 pl-10 text-[14px] outline-none',
            'transition-colors placeholder:text-ghost focus:border-accent',
            isPassword ? 'pr-11' : 'pr-3.5'
          )}
        />
        {isPassword ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShown((v) => !v)}
            aria-label={shown ? 'Hide password' : 'Show password'}
            className="absolute top-1/2 right-2 grid h-8 w-8 -translate-y-1/2 place-items-center rounded-[calc(8px*var(--radius-mul,1))] text-ghost transition-colors hover:text-dim"
          >
            <Icon name={shown ? 'eyeOff' : 'eye'} size={15} />
          </button>
        ) : null}
      </div>

      {meter && value ? <Strength value={value} /> : null}
      {hint ? <p className="mt-1.5 text-[11.5px] text-ghost">{hint}</p> : null}
    </div>
  );
}

/**
 * How much password there is.
 *
 * Length, and whether it is more than one kind of character. Deliberately not
 * a rule you have to satisfy - refusing a long passphrase for having no digit
 * is how people end up with Password1!.
 */
function Strength({ value }: { value: string }) {
  const kinds = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((r) => r.test(value)).length;
  const score = Math.min(4, Math.max(0, Math.floor(value.length / 5) + (kinds > 2 ? 1 : 0)));
  const labels = ['Too short', 'Weak', 'Getting there', 'Good', 'Strong'] as const;
  const colours = ['var(--bad)', 'var(--bad)', 'var(--warn)', 'var(--good)', 'var(--good)'] as const;
  const colour = colours[score] ?? colours[0];

  return (
    <div className="mt-2 flex items-center gap-2.5">
      <span className="flex flex-1 gap-1">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="h-[3px] flex-1 rounded-full transition-colors duration-200"
            style={{ background: i < score ? colour : 'var(--surface-3)' }}
          />
        ))}
      </span>
      <span className="text-[11px] font-semibold" style={{ color: colour }}>
        {labels[score]}
      </span>
    </div>
  );
}

/**
 * The box you type a code into.
 *
 * Wide, spaced and monospaced, because six digits read off a phone are
 * checked character by character. Recovery codes go in the same box, so it is
 * not restricted to digits.
 */
export function CodeInput({ value, onChange, autoFocus = true }: {
  value: string;
  onChange: (next: string) => void;
  autoFocus?: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  return (
    <input
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value.toUpperCase().slice(0, 20))}
      autoComplete="one-time-code"
      inputMode="numeric"
      spellCheck={false}
      placeholder="000000"
      className={cn(
        'h-[54px] w-full rounded-[calc(12px*var(--radius-mul,1))] border border-line bg-surface-2 px-4',
        'text-center font-mono text-[21px] tracking-[.34em] outline-none',
        'transition-colors placeholder:tracking-[.34em] placeholder:text-ghost focus:border-accent'
      )}
    />
  );
}

function Spinner() {
  return (
    <span className="h-[15px] w-[15px] animate-spin rounded-full border-2 border-current border-t-transparent opacity-70" />
  );
}
